import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowUpDown, Wallet, ReceiptText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TokenIcon } from './TokenIcon';

interface BuyTokensModalProps {
    isOpen: boolean;
    onClose: () => void;
    onPurchase: (amount: number) => void;
}

const EXCHANGE_RATE_ARS = 0.5; // 1 Token = 0.5 ARS
// 1 ARS = 2 Tokens

export const BuyTokensModal = ({ isOpen, onClose, onPurchase }: BuyTokensModalProps) => {
    // Mode: true = Tokens -> Price, false = Price -> Tokens
    const [isTokenToPrice, setIsTokenToPrice] = useState(true);

    // The value being typed in the top input
    const [inputValue, setInputValue] = useState<string>('10000');

    // The calculated value for the bottom input
    const [calculatedValue, setCalculatedValue] = useState<string>('');

    // Calculate whenever input or mode changes
    useEffect(() => {
        const value = parseFloat(inputValue.replace(/\D/g, '') || '0');

        if (isTokenToPrice) {
            // Input is Tokens, Output is Price
            const price = value * EXCHANGE_RATE_ARS;
            setCalculatedValue(price > 0 ? `$ ${price.toLocaleString('es-AR')}` : '');
        } else {
            // Input is Price, Output is Tokens
            const tokens = value / EXCHANGE_RATE_ARS;
            setCalculatedValue(tokens > 0 ? Math.round(tokens).toLocaleString('es-AR') : '');
        }
    }, [inputValue, isTokenToPrice]);

    const handleSwap = () => {
        // When swapping, the bottom value becomes the top value (so the user continues editing what they saw)
        // Clean the calculated value string to just numbers for the new input state
        const rawCalculated = calculatedValue.replace(/[^0-9]/g, '');

        if (rawCalculated) {
            setInputValue(rawCalculated);
        }

        setIsTokenToPrice(!isTokenToPrice);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handlePurchase = () => {
        // Calculate final amount in tokens regardless of mode
        let tokenAmount = 0;
        const value = parseFloat(inputValue.replace(/\D/g, '') || '0');

        if (isTokenToPrice) {
            tokenAmount = value;
        } else {
            tokenAmount = value / EXCHANGE_RATE_ARS;
        }

        if (tokenAmount > 0) {
            onPurchase(Math.round(tokenAmount));
            // Reset after purchase (optional, keeping mode or resetting default?) 
            // Let's reset to default for next time
            setTimeout(() => {
                setInputValue('10000');
                setIsTokenToPrice(true);
            }, 500);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/50 backdrop-blur-md z-50 transition-all"
                    />
                    <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none p-4">
                        <motion.div
                            role="dialog"
                            aria-modal="true"
                            aria-label="Comprar tokens"
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 10 }}
                            className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden pointer-events-auto border border-gray-100"
                        >
                            {/* Header */}
                            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">Recargar créditos</h3>
                                    <p className="text-sm text-gray-500">Acreditación instantánea en tu plan</p>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Body */}
                            <div className="p-6 space-y-6">
                                {/* Converter */}
                                <div className="space-y-4 relative">

                                    {/* Top Input (Editable) */}
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold uppercase text-gray-400 tracking-wider">
                                            {isTokenToPrice ? 'Cantidad de créditos' : 'Monto en Pesos (ARS)'}
                                        </label>
                                        <div className="relative">
                                            <Input
                                                type="text"
                                                value={inputValue}
                                                onChange={handleInputChange}
                                                className="h-14 text-2xl font-bold pl-12 border-gray-200 focus:border-violet-500 focus:ring-violet-500/20"
                                            />
                                            <div className={`absolute left-4 top-1/2 -translate-y-1/2 ${isTokenToPrice ? 'text-yellow-500' : 'text-gray-400'}`}>
                                                {isTokenToPrice ? (
                                                    <TokenIcon size={24} />
                                                ) : (
                                                    <span className="text-2xl font-sans">$</span>
                                                )}
                                            </div>


                                        </div>
                                    </div>

                                    {/* Swap Button */}
                                    <div className="flex justify-center -my-2 relative z-10">
                                        <button
                                            onClick={handleSwap}
                                            className="bg-gray-50 hover:bg-white p-2 rounded-full border border-gray-200 hover:border-violet-200 text-gray-400 hover:text-violet-600 shadow-sm hover:shadow transition-all group active:scale-95"
                                        >
                                            <ArrowUpDown size={16} className="group-hover:rotate-180 transition-transform duration-300" />
                                        </button>
                                    </div>

                                    {/* Bottom Input (Read Only) */}
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold uppercase text-gray-400 tracking-wider">
                                            {isTokenToPrice ? 'Total a pagar (ARS)' : 'Créditos recibidos'}
                                        </label>
                                        <div className="relative">
                                            <Input
                                                readOnly
                                                value={calculatedValue}
                                                className="h-14 text-2xl font-bold pl-12 bg-gray-50 border-gray-200 text-gray-600 cursor-not-allowed"
                                            />
                                            <div className={`absolute left-4 top-1/2 -translate-y-1/2 ${!isTokenToPrice ? 'text-yellow-500' : 'text-gray-400'}`}>
                                                {!isTokenToPrice ? (
                                                    <TokenIcon size={24} />
                                                ) : (
                                                    <Wallet size={24} />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Billing Info */}
                                <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 flex gap-3 text-blue-700">
                                    <ReceiptText className="shrink-0 mt-0.5" size={20} />
                                    <div className="text-sm">
                                        <p className="font-semibold mb-0.5">Facturación post-pago</p>
                                        <p className="text-blue-600/80">Los créditos se acreditan al instante; el cargo se suma a tu próxima factura mensual.</p>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="p-4 border-t border-gray-100 bg-gray-50/50 flex gap-3">
                                <Button
                                    onClick={onClose}
                                    variant="outline"
                                    className="flex-1 h-11"
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    onClick={handlePurchase}
                                    className="flex-1 h-11 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-lg shadow-violet-200 border-none"
                                >
                                    Confirmar recarga
                                </Button>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};
